// Guessed IPs are not observed IPs.
//
// For an Exchange or SharePoint event recorded with a Microsoft service address and no
// session, the investigation runbook could only GUESS where it came from:
//   TimeWindowCorrelation -- the latest successful sign-in in the previous 15 minutes;
//   UserIPCorrelation     -- the user's only known IP in the period.
// Until runbook 2026-10-13.1 the guess overwrote IPAddress, so the timeline, the IP
// colouring, attacker attribution, the attacker window and the reports all treated it as
// the recorded address. On IR-20261013-levig-001 that put Barracuda Networks' mailbox scan
// on the AiTM attacker's IP: "the attacker read the mail", when no attacker reached the
// mailbox (Silver handoff 2026-10-13).
//
// `demoteGuessedIP` puts the RECORDED address back in IPAddress and moves the guess to
// GuessedIP. It runs once, in normalizeResults, so every consumer reads the honest value
// from the same field it always read. Idempotent: a blob from runbook 2026-10-13.1 or later
// already carries GuessedIP and is returned unchanged, and an older blob gets the same shape.

export const GUESSED_IP_METHODS = new Set(['TimeWindowCorrelation', 'UserIPCorrelation']);

export function isGuessedIpMethod(method) {
  return GUESSED_IP_METHODS.has(String(method || ''));
}

export function demoteGuessedIP(evt) {
  if (!evt || typeof evt !== 'object') return evt;
  const method = evt.IPResolutionMethod || '';
  if (!isGuessedIpMethod(method)) return evt;
  if (evt.GuessedIP) return evt; // already demoted by the runbook
  const guessed = evt.IPAddress || '';
  return {
    ...evt,
    GuessedIP: guessed,
    GuessedIPMethod: method,
    IPAddress: evt.OriginalIPAddress || '',
    ResolvedUserIP: '',
  };
}

/** One line for the analyst: where the guess came from. */
export function guessedIpBasis(evt) {
  const m = evt?.GuessedIPMethod || evt?.IPResolutionMethod || '';
  if (m === 'TimeWindowCorrelation') return 'the user’s most recent sign-in in the 15 minutes before';
  if (m === 'UserIPCorrelation') return 'the only IP the user signed in from during the case';
  return 'proximity to the user’s sign-ins';
}

/**
 * The app that made an app-only read with no session (Exchange CallingAppName), when the
 * runbook left it unresolved because that app never signed in as the user. Empty otherwise.
 */
export function appOnlyReader(evt) {
  if (!evt || evt.AADSessionId) return '';
  if ((evt.IPResolutionMethod || '') !== 'Unresolved-ServiceIP') return '';
  return evt.CallingAppName || '';
}
